import { CouponDetail } from '../../types/cupom';
import { Ticket, TicketType } from '../../types/ticket';
import { Rule } from '../types/Rule';
import { RuleContext } from '../types/RuleContext';

/**
 * Applies a coupon to the tickets in the cart, updating their accumulated values based on the coupon availability.
 * @param cart - The array of tickets in the cart.
 * @param coupon - The coupon to be applied, if available.
 * @returns The updated cart with the accumulated values for each ticket type.
*/
export class ApplyCouponRuleIfExists implements Rule {
  apply(context: RuleContext): Ticket[] {
    const { cart, couponApplied } = context;
    let couponLimitPerTicket = 0;
    let unlimited = false;

    if (couponApplied) {
      ({ couponLimitPerTicket, unlimited } = this.getCouponLimits(couponApplied));
    }

    const ticketsWithActiveCoupon = this.extractCouponListByType({ list : cart || [], type : 'withCoupon' });
    const ticketsNoCouponAvailable = this.extractCouponListByType({ list : cart || [], type : 'noCoupon' });

    this.processTicketsWithActiveCoupon(ticketsWithActiveCoupon, couponLimitPerTicket, unlimited);
    this.processTicketsNoCouponAvailable(ticketsNoCouponAvailable);

    return this.updateCart(cart || [], ticketsWithActiveCoupon, ticketsNoCouponAvailable);
  }

  private getCouponLimits(couponApplied: CouponDetail) {
    let couponLimitPerTicket = 0;
    let unlimited = false;
    if (couponApplied) {
      couponLimitPerTicket = couponApplied.coupon.limit_ticket;
      unlimited = couponApplied.coupon.limit_ticket === 0;
    }
    return { couponLimitPerTicket, unlimited };
  }

  /**
   * Extracts a list of ticket types based on whether they have a coupon or not and sorts them by original price in descending order.
   * @param list - The array of tickets to filter and sort.
   * @param type - The type of tickets to extract, either 'withCoupon' or 'noCoupon'.
   * @returns The sorted array of ticket types with or without coupons.
  */
  private extractCouponListByType = ({ list, type }: { list: Ticket[], type: 'withCoupon' | 'noCoupon' }) => {
    return list.flatMap((item) => item.type.filter((ticket) => {
      if (type === 'withCoupon') return ticket.tagType;
      return !ticket.tagType;
    })).sort((a, b) => b.originalPrice - a.originalPrice);
  };

  private processTicketsWithActiveCoupon(tickets: TicketType[], couponLimitPerTicket: number, unlimited: boolean) {
    tickets.forEach((item) => {
      item.accumulatedValueInCart = 0;
      item.checkoutPrice = [];
      for (let index = 0; index < item.quantityInCart!; index++) {
        if (unlimited || couponLimitPerTicket > 0 && item.quantityInCart >= (index + 1)) {
          item.accumulatedValueInCart += item.price;
          item.checkoutPrice = [...item.checkoutPrice, item.price];
          couponLimitPerTicket--;
        } else {
          this.applyOriginalPrice(item, couponLimitPerTicket);
        }
      }
    });
  }

  private applyOriginalPrice(item: TicketType, couponLimitPerTicket: number) {
    if (couponLimitPerTicket > 0) {
      item.accumulatedValueInCart += item.price;
      item.checkoutPrice = [...item.checkoutPrice, item.price];
    } else {
      item.accumulatedValueInCart += item.originalPrice;
      item.checkoutPrice = [...item.checkoutPrice, item.originalPrice];
    }
  }

  private processTicketsNoCouponAvailable(tickets: TicketType[]) {
    tickets.forEach((item) => {
      item.accumulatedValueInCart = 0;
      for (let index = 0; index < item.quantityInCart!; index++) {
        item.accumulatedValueInCart += item.price;
        item.price = item.originalPrice;
        item.tagType = undefined;
        item.checkoutPrice = [...item.checkoutPrice, item.price];
      }
    });
  }

  private updateCart(cart: Ticket[], ticketsWithActiveCoupon: TicketType[], ticketsNoCouponAvailable: TicketType[]) {
    return (cart || []).map((item) => {
      return {
        ...item,
        type : item.type.map((type) => {
          const itemUpdated = [...ticketsWithActiveCoupon, ...ticketsNoCouponAvailable]
            .find((ticket) => ticket.id === type.id);

          return itemUpdated || type;
        })
      };
    });
  }
}
